import { HttpErrorResponse } from '@angular/common/http'
import { computed, Injectable, signal } from '@angular/core'
import { ReplenishmentsRepository } from '../replenishments.repository'
import { REPLENISHMENT_DEFAULT_FIELDS } from '../consts/replenishment-fields.const'
import { Replenishment } from '../models/replenishment.entity'
import {
  ReplenishmentsListingData,
  ReplenishmentsListingPage,
} from '../models/replenishment-listing.model'
import { ReplenishmentSearchParams } from '../models/replenishment-search.model'
import { ReplenishmentsService } from '../replenishments.service'
import { ReplenishmentField } from '../models/replenishment.types'
import { Observable } from 'rxjs'
import { WarehousesService } from '../../warehouses'
import { LocationsService } from '../../locations'
import { ProductsService } from '../../products'
import { UsersService } from '../../users'
import { mergeById } from '../../../libs/filter.lib'

interface ReplenishmentsListingState {
  data?: Replenishment[]
  extData?: ReplenishmentsListingData
  dropdownOptions?: ReplenishmentsListingData
  totalCount?: number
  fields?: ReplenishmentField[]
  filters?: ReplenishmentSearchParams
  error?: HttpErrorResponse
  toReload: boolean
  isLoading: boolean
  isInitialized: boolean
}

const REPLENISHMENTS_INITIAL_STATE: ReplenishmentsListingState = {
  fields: REPLENISHMENT_DEFAULT_FIELDS,
  toReload: false,
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class ReplenishmentsListingStore {
  constructor(
    private replenishmentsRepository: ReplenishmentsRepository,
    private replenishmentsService: ReplenishmentsService,
    private warehousesService: WarehousesService,
    private locationsService: LocationsService,
    private productsService: ProductsService,
    private usersService: UsersService,
  ) {}

  private state = signal<ReplenishmentsListingState>(
    REPLENISHMENTS_INITIAL_STATE,
  )

  // Selectors
  readonly replenishments = computed(() => this.state().data || [])
  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly fields = computed(() => this.state().fields)
  readonly users = computed(() => this.state().extData?.users)
  readonly warehouses = computed(() => this.state().extData?.warehouses)
  readonly locations = computed(() => this.state().extData?.locations)
  readonly products = computed(() => this.state().extData?.products)

  // Effects

  async search(
    params?: ReplenishmentSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.replenishmentsRepository
        .searchReplenishments$(
          params ?? {},
          this.state().fields ?? [],
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            resolve()
            this.setData(data)
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async cancel(replenishment: Replenishment): Promise<boolean> {
    if (
      replenishment.status !== 'PENDING' &&
      replenishment.status !== 'PROCESSING'
    ) {
      this.replenishmentsRepository.notify({ code: 'WRONG_STATUS' })
      return false
    }

    if (replenishment.movedQty > 0) {
      this.replenishmentsRepository.notify({ code: 'CANNOT_CANCEL_MOVED_QTY' })
      return false
    }

    return new Promise<boolean>((resolve) => {
      this.replenishmentsRepository
        .alert('REPLENISHMENT_CANCELATION', replenishment)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.replenishmentsService.cancel$(replenishment._id).subscribe({
              next: () => {
                resolve(true)
                this.replenishmentsRepository.notify({
                  code: 'REPLENISHMENT_CANCELED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof ReplenishmentsListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<
      NonNullable<ReplenishmentsListingData[LookupKey]>
    >

    const queryKey = (event.dataDisplayField ?? 'name') + ':ct'
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      locations: () => this.locationsService.list$(query),
      products: () => this.productsService.list$(query),
      users: () => this.usersService.list$(query),
      warehouses: () => this.warehousesService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  // Reducers

  setData(data: ReplenishmentsListingPage) {
    const extData = this.extData()
    const newExtData = {
      warehouses: mergeById(extData?.warehouses, data.extData?.warehouses),
      products: mergeById(extData?.products, data.extData?.products),
      locations: mergeById(extData?.locations, data.extData?.locations),
      users: mergeById(extData?.users, data.extData?.users),
    }
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: newExtData,
      dropdownOptions: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: ReplenishmentSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: ReplenishmentSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  removeFilter(filter: ReplenishmentSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof ReplenishmentSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setFields(fields?: ReplenishmentField[]) {
    this.state.set({ ...this.state(), fields })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }
}
